import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Users,
  MapPin,
  Clock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Activity,
  TrendingUp,
  TrendingDown,
  Calendar,
  Search,
  Filter,
  RefreshCw,
  ChevronRight,
  User,
  Navigation,
  Target,
  Eye,
  X,
  Map,
} from 'lucide-react';
import { toast } from 'sonner';
import { wellCheckService, WellCheck, WellCheckStats, PumperCheckStats } from '../utils/navigation/wellCheckService';

interface WellCheckDashboardProps {
  wells: Array<{
    id: string;
    name: string;
    apiNumber?: string;
    latitude?: number;
    longitude?: number;
    assignedPumperId?: string;
  }>;
  pumpers: Array<{
    id: string;
    name: string;
    email?: string;
  }>;
  onViewWell?: (wellId: string) => void;
  onViewPumper?: (pumperId: string) => void;
}

export default function WellCheckDashboard({
  wells,
  pumpers,
  onViewWell,
  onViewPumper,
}: WellCheckDashboardProps) {
  // Data state
  const [todaysChecks, setTodaysChecks] = useState<WellCheck[]>([]);
  const [wellStats, setWellStats] = useState<WellCheckStats[]>([]);
  const [pumperStats, setPumperStats] = useState<PumperCheckStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'verified' | 'unverified' | 'suspicious'>('all');
  const [filterPumper, setFilterPumper] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'checks' | 'wells' | 'pumpers'>('checks');

  // Detail view
  const [selectedCheck, setSelectedCheck] = useState<WellCheck | null>(null);

  // Load data
  useEffect(() => {
    loadData();

    // Subscribe to real-time updates
    const unsubscribe = wellCheckService.subscribeToTodaysChecks((checks) => {
      setTodaysChecks(checks);
    });

    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [checks, wStats, pStats] = await Promise.all([
        wellCheckService.getTodaysChecks(),
        wellCheckService.getWellCheckStats(wells.map(w => w.id)),
        wellCheckService.getPumperStats(pumpers.map(p => p.id)),
      ]);

      setTodaysChecks(checks);
      setWellStats(wStats);
      setPumperStats(pStats);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load check data');
    } finally {
      setLoading(false);
    }
  };

  // Computed stats
  const stats = useMemo(() => {
    const verified = todaysChecks.filter(c => c.verificationStatus === 'verified').length;
    const unverified = todaysChecks.filter(c => c.verificationStatus === 'unverified').length;
    const suspicious = todaysChecks.filter(c => c.verificationStatus === 'suspicious').length;
    const dataEntryCompleted = todaysChecks.filter(c => c.dataEntryCompleted).length;
    
    const uniqueWells = new Set(todaysChecks.map(c => c.wellId)).size;
    const uniquePumpers = new Set(todaysChecks.map(c => c.pumperId)).size;
    
    const wellsCovered = (uniqueWells / wells.length) * 100;
    
    return {
      totalChecks: todaysChecks.length,
      verified,
      unverified,
      suspicious,
      dataEntryCompleted,
      uniqueWells,
      uniquePumpers,
      wellsCovered,
      wellsRemaining: wells.length - uniqueWells,
    };
  }, [todaysChecks, wells.length]);

  // Filtered checks
  const filteredChecks = useMemo(() => {
    return todaysChecks.filter(check => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!check.wellName.toLowerCase().includes(query) &&
            !check.pumperName.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== 'all' && check.verificationStatus !== filterStatus) {
        return false;
      }

      // Pumper filter
      if (filterPumper !== 'all' && check.pumperId !== filterPumper) {
        return false;
      }

      return true;
    });
  }, [todaysChecks, searchQuery, filterStatus, filterPumper]);

  // Wells that haven't been checked today
  const uncheckedWells = useMemo(() => {
    const checkedWellIds = new Set(todaysChecks.map(c => c.wellId));
    return wells.filter(w => !checkedWellIds.has(w.id));
  }, [wells, todaysChecks]);

  const getVerificationBadge = (status: WellCheck['verificationStatus']) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>;
      case 'unverified':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="h-3 w-3 mr-1" />Unverified</Badge>;
      case 'suspicious':
        return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />Suspicious</Badge>;
    }
  };

  const formatTime = (date: Date | undefined) => {
    if (!date) return '-';
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatDuration = (minutes: number | undefined) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Activity className="h-7 w-7 mr-2 text-blue-600" />
            Well Check Dashboard
          </h2>
          <p className="text-gray-600 mt-1">
            Real-time tracking of pumper well visits
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Updated time and refresh button removed */}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Checks</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalChecks}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">GPS Verified</p>
                <p className="text-2xl font-bold text-green-600">{stats.verified}</p>
              </div>
              <Target className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Suspicious</p>
                <p className="text-2xl font-bold text-red-600">{stats.suspicious}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Data Entered</p>
                <p className="text-2xl font-bold text-purple-600">{stats.dataEntryCompleted}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Wells Covered</p>
                <p className="text-2xl font-bold text-blue-600">{stats.uniqueWells}/{wells.length}</p>
              </div>
              <MapPin className="h-8 w-8 text-blue-600" />
            </div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${stats.wellsCovered}%` }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Pumpers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.uniquePumpers}</p>
              </div>
              <Users className="h-8 w-8 text-gray-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'checks' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('checks')}
          >
            <Clock className="h-4 w-4 mr-1" />
            Today's Checks
          </Button>
          <Button
            variant={viewMode === 'wells' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('wells')}
          >
            <MapPin className="h-4 w-4 mr-1" />
            By Well
          </Button>
          <Button
            variant={viewMode === 'pumpers' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('pumpers')}
          >
            <Users className="h-4 w-4 mr-1" />
            By Pumper
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search wells or pumpers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>

          <Select value={filterStatus} onValueChange={(v: any) => setFilterStatus(v)}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="unverified">Unverified</SelectItem>
              <SelectItem value="suspicious">Suspicious</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterPumper} onValueChange={setFilterPumper}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Pumper" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pumpers</SelectItem>
              {pumpers.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'checks' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Check List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Today's Well Checks ({filteredChecks.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {filteredChecks.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No checks recorded yet today</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {filteredChecks.map((check) => (
                      <div
                        key={check.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => setSelectedCheck(check)}
                      >
                        <div className="flex items-center space-x-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            check.verificationStatus === 'verified' ? 'bg-green-100' :
                            check.verificationStatus === 'suspicious' ? 'bg-red-100' :
                            'bg-yellow-100'
                          }`}>
                            <MapPin className={`h-6 w-6 ${
                              check.verificationStatus === 'verified' ? 'text-green-600' :
                              check.verificationStatus === 'suspicious' ? 'text-red-600' :
                              'text-yellow-600'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{check.wellName}</p>
                            <p className="text-sm text-gray-600 flex items-center">
                              <User className="h-3 w-3 mr-1" />
                              {check.pumperName}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              {formatTime(check.checkInTime)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {check.durationMinutes ? `${check.durationMinutes}m on site` : 'Active'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-1">
                            {getVerificationBadge(check.verificationStatus)}
                            {check.dataEntryCompleted && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                Data ✓
                              </Badge>
                            )}
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Unchecked Wells */}
          <div>
            <Card className={uncheckedWells.length > 0 ? 'border-orange-200' : ''}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <AlertCircle className={`h-5 w-5 mr-2 ${
                    uncheckedWells.length > 0 ? 'text-orange-500' : 'text-green-500'
                  }`} />
                  Wells Not Checked ({uncheckedWells.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {uncheckedWells.length === 0 ? (
                  <div className="text-center py-8 text-green-600">
                    <CheckCircle className="h-12 w-12 mx-auto mb-2" />
                    <p className="font-medium">All wells checked!</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {uncheckedWells.map((well) => (
                      <div
                        key={well.id}
                        className="flex items-center justify-between p-3 bg-orange-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{well.name}</p>
                          {well.apiNumber && (
                            <p className="text-xs text-gray-500">API: {well.apiNumber}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onViewWell?.(well.id)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {viewMode === 'wells' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Well Check Statistics (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Well</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Today</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">This Week</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Avg Duration</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Missed Days</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Last Check</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {wellStats.map((stat) => (
                    <tr key={stat.wellId} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{stat.wellName}</span>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4">
                        <Badge className={stat.checksToday > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                          {stat.checksToday}
                        </Badge>
                      </td>
                      <td className="text-center py-3 px-4">{stat.checksThisWeek}</td>
                      <td className="text-center py-3 px-4">{formatDuration(stat.averageDuration)}</td>
                      <td className="text-center py-3 px-4">
                        <span className={stat.missedCheckDays > 2 ? 'text-red-600 font-medium' : ''}>
                          {stat.missedCheckDays}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="text-sm">{stat.lastCheckTime?.toLocaleDateString() || 'Never'}</p>
                          <p className="text-xs text-gray-500">{stat.lastCheckedBy || '-'}</p>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4">
                        <Button size="sm" variant="ghost" onClick={() => onViewWell?.(stat.wellId)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {viewMode === 'pumpers' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Pumper Performance (Last 7 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Pumper</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Today</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">This Week</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Avg Time/Well</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Verified</th>
                    <th className="text-center py-3 px-4 font-medium text-gray-600">Suspicious</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pumperStats.map((stat) => (
                    <tr key={stat.pumperId} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="font-medium">{stat.pumperName}</span>
                        </div>
                      </td>
                      <td className="text-center py-3 px-4">
                        <Badge className="bg-blue-100 text-blue-800">
                          {stat.totalChecksToday} ({stat.wellsVisitedToday.length} wells)
                        </Badge>
                      </td>
                      <td className="text-center py-3 px-4">{stat.totalChecksThisWeek}</td>
                      <td className="text-center py-3 px-4">{formatDuration(stat.averageTimePerWell)}</td>
                      <td className="text-center py-3 px-4">
                        <span className="text-green-600 font-medium">{stat.verifiedChecks}</span>
                      </td>
                      <td className="text-center py-3 px-4">
                        {stat.suspiciousChecks > 0 ? (
                          <Badge className="bg-red-100 text-red-800">{stat.suspiciousChecks}</Badge>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="text-right py-3 px-4">
                        <Button size="sm" variant="ghost" onClick={() => onViewPumper?.(stat.pumperId)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Check Detail Modal */}
      {selectedCheck && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Check Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedCheck(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedCheck.verificationStatus === 'verified' ? 'bg-green-100' :
                    selectedCheck.verificationStatus === 'suspicious' ? 'bg-red-100' :
                    'bg-yellow-100'
                  }`}>
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-bold text-lg">{selectedCheck.wellName}</p>
                    {selectedCheck.wellApiNumber && (
                      <p className="text-sm text-gray-500">API: {selectedCheck.wellApiNumber}</p>
                    )}
                  </div>
                </div>
                {getVerificationBadge(selectedCheck.verificationStatus)}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Pumper</p>
                  <p className="font-medium">{selectedCheck.pumperName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Check-in Time</p>
                  <p className="font-medium">{formatTime(selectedCheck.checkInTime)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Distance from Well</p>
                  <p className="font-medium">{Math.round(selectedCheck.distanceFromWell)} feet</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-medium">
                    {selectedCheck.durationMinutes 
                      ? formatDuration(selectedCheck.durationMinutes)
                      : 'Still on site'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">GPS Accuracy</p>
                  <p className="font-medium">±{Math.round(selectedCheck.checkInLocation.accuracy)}m</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Entry</p>
                  <p className="font-medium">
                    {selectedCheck.dataEntryCompleted ? (
                      <span className="text-green-600">✓ Completed</span>
                    ) : (
                      <span className="text-gray-400">Not completed</span>
                    )}
                  </p>
                </div>
              </div>

              {selectedCheck.verificationNotes && (
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    {selectedCheck.verificationNotes}
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setSelectedCheck(null)}>
                  Close
                </Button>
                <Button onClick={() => {
                  onViewWell?.(selectedCheck.wellId);
                  setSelectedCheck(null);
                }}>
                  <MapPin className="h-4 w-4 mr-1" />
                  View Well
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
